const post = {
  slug: 'idempotency-is-a-backend-superpower',
  number: 13,
  title: 'Idempotency Is a Backend Superpower',
  date: '2026-05-16',
  readTime: '7 min read',
  views: 77,
  category: 'backend',
  tags: ['Backend', 'APIs', 'Reliability'],
  excerpt:
    'Networks retry. Users double-click. Queues redeliver. Idempotency is the one idea that lets all of that happen without doing the work twice, and it is worth designing for from the very start.',
  cover: null,
  content: `
The first production incident that really taught me about distributed systems was a customer charged twice for the same payment. Nothing crashed. No error was logged. The client had simply timed out waiting for our response, retried like any sensible client would, and we processed both requests as if they were different. Two charges, one angry customer, and a long afternoon proving to ourselves what had happened.

That bug has a name, or rather its absence does. The fix is idempotency, and it is one of the highest-leverage ideas you can build into a backend. Once I understood it properly, a whole class of scary failures turned into non-events.

## The same request will always arrive twice

Start from a fact that never stops being true: the same request will arrive more than once. A network times out and the client retries. A user double-clicks the button. A message queue redelivers because it never got the acknowledgement. None of these are bugs. They are the normal, correct behaviour of unreliable networks, and your backend has to be ready for them.

The dangerous operations are the ones that change something relative to its current state. Charging a card, incrementing a balance, creating an order. Run those twice and you have done real damage. Reading a value or setting it to a fixed amount is naturally safe to repeat. The whole game is making the dangerous operations safe to repeat too.

## The idempotency key

The standard tool is a key. The client generates a unique value for each genuine operation and sends it with the request. The server records that it has seen that key. If the same key shows up again, the server returns the original result instead of doing the work a second time.

\`\`\`text
client: "charge 5000, idempotency-key: a1b2c3"
server: never seen a1b2c3 -> charge, store the result under a1b2c3
client: (timeout, retries) "charge 5000, idempotency-key: a1b2c3"
server: already seen a1b2c3 -> return the stored result, do NOT charge again
\`\`\`

The key is what tells the server "this is the same operation, not a new one." Without it, two identical requests are indistinguishable, and the server has no choice but to treat the retry as fresh work.

## Let the database enforce it

The trap is checking for the key and then acting in two separate steps. Two copies of the request can both check, both find nothing, and both proceed, which is exactly the double charge you were trying to prevent. The window is tiny, and at scale tiny windows happen constantly.

The fix is to let the database enforce uniqueness atomically, rather than checking first in your own code. Make the insert itself the gate.

\`\`\`sql
-- The unique constraint turns a duplicate into a no-op, with no race
INSERT INTO payments (idempotency_key, amount, status)
VALUES ($1, $2, 'pending')
ON CONFLICT (idempotency_key) DO NOTHING;
\`\`\`

The unique constraint on the key means a duplicate cannot create a second row, no matter how many copies arrive or how close together the retries land. The database is built to resolve these conflicts correctly under load. Hand-rolled check-then-act logic is not, and it fails exactly when traffic is highest and you can least afford it.

## Where the key comes from matters

For the guarantee to hold, the key has to identify the operation, not the attempt. The client generates one key per real intention to pay, and reuses that same key on every retry of that payment. A fresh key on each retry defeats the entire mechanism, because now every attempt looks like a new operation again.

This is worth being explicit about in your API. Document that the client owns the key, that it must stay stable across retries of the same logical request, and that a new logical request gets a new key. Get that contract right and retries become free safety. Get it wrong and the keys are just decoration.

## Make it the default, not the exception

The mistake is treating idempotency as something you bolt onto one or two critical endpoints after an incident. By then you have already paid for the lesson, the way we did with that double charge. Any operation that creates or moves something of value should be designed to be safely repeatable from the start.

- Identify every write that has real-world consequences.
- Require an idempotency key on each of them.
- Enforce uniqueness in the database, not in application logic.
- Make sure the client reuses the key across retries of the same operation.
- Return the original result on a repeat, so the caller sees success, not a confusing error.

It costs you a key and a unique constraint. In return, retries stop being scary, queue redelivery becomes harmless, and a flaky network turns into a non-event instead of a support ticket about money moving twice. For that price, it is one of the best trades in backend engineering.
`,
};

export default post;
